/**
 * Edit rule… (v1 completion I §9, the mock-up's state 9): on or off, who it goes
 * to, the department, the due working days and the priority. For Time's rules
 * the due is stated, not editable: Time says when a decision is due. Every change is
 * checked by the server's dry run as it is made: the before and after, what
 * turning it off clears, and each refusal bound to its field. Save sends only
 * what changed, to the command's own href.
 */
import { useEffect, useMemo, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import type {
  AlertCommand,
  AlertDryRun,
  AttentionRuleView,
  RuleOwner,
  UpdateAttentionRuleInput,
} from "@shared-core/data/needs-attention-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { refusalWords } from "@shared-core/domain/needs-attention";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";

const selectClass =
  "h-9 w-full rounded-md border border-border bg-card px-3 text-sm";
const PRIORITIES = [
  ["normal", "Normal"],
  ["low", "Low"],
  ["high", "High"],
  ["urgent", "Urgent"],
] as const;

interface Form {
  is_enabled: boolean;
  owner: RuleOwner;
  department_slug: string;
  due_working_days: string;
  priority: string;
}

function formOf(rule: AttentionRuleView): Form {
  return {
    is_enabled: rule.state === "on",
    owner: rule.owner,
    department_slug: rule.department_slug,
    due_working_days: String(rule.due_working_days),
    priority: rule.priority,
  };
}

/** Only what differs from the rule as it stands. */
function changesOf(
  rule: AttentionRuleView,
  form: Form,
): UpdateAttentionRuleInput {
  const before = formOf(rule);
  const changes: UpdateAttentionRuleInput = {};
  if (form.is_enabled !== before.is_enabled)
    changes.is_enabled = form.is_enabled;
  if (form.owner !== before.owner) changes.owner = form.owner;
  if (form.department_slug !== before.department_slug)
    changes.department_slug = form.department_slug;
  if (form.due_working_days !== before.due_working_days)
    changes.due_working_days = Number(form.due_working_days);
  if (form.priority !== before.priority)
    changes.priority = form.priority as UpdateAttentionRuleInput["priority"];
  return changes;
}

export interface EditRuleDialogProps {
  code: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (summary: string) => void;
}

export function EditRuleDialog({
  code,
  open,
  onOpenChange,
  onSaved,
}: EditRuleDialogProps) {
  const adapter = useDataAdapter();
  const [rule, setRule] = useState<AttentionRuleView | null>(null);
  const [departments, setDepartments] = useState<API.Department[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [preview, setPreview] = useState<AlertDryRun | null>(null);
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRule(null);
    setForm(null);
    setPreview(null);
    setSaveErrors({});
    setBanner(null);
    Promise.all([adapter.getAttentionRule(code), adapter.listDepartments(true)])
      .then(([found, active]) => {
        if (cancelled) return;
        setRule(found);
        setForm(found ? formOf(found) : null);
        setDepartments(active);
        if (!found) setBanner("This rule is not there any more.");
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setBanner(refusalWords(err) ?? "The rule did not load.");
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, code, open]);

  const edit: AlertCommand | undefined = rule?.commands.find(
    (c) => c.name === "needs_attention.update_attention_rule",
  );
  const changes = useMemo(
    () => (rule && form ? changesOf(rule, form) : {}),
    [rule, form],
  );
  const settled = useDebouncedValue(changes, 250);

  useEffect(() => {
    if (!edit || Object.keys(settled).length === 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    adapter
      .dryRunAlertCommand(edit, settled)
      .then((run) => {
        if (!cancelled) setPreview(run);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setBanner(refusalWords(err) ?? "The change could not be checked.");
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, edit, settled]);

  const fieldErrors: Record<string, string> = {
    ...Object.fromEntries(
      (preview?.errors ?? []).map((e) => [e.field, e.message]),
    ),
    ...saveErrors,
  };
  const errorCount = Object.keys(fieldErrors).length;
  const hasChanges = Object.keys(changes).length > 0;
  const canSave =
    Boolean(edit) &&
    hasChanges &&
    errorCount === 0 &&
    !isSaving &&
    preview?.can_commit !== false;
  const changesBlock = preview?.display.find(
    (block) => block.kind === "before_after",
  );
  const notes = (preview?.display ?? []).flatMap((block) =>
    block.kind === "note" ? block.lines : [],
  );

  const set = (patch: Partial<Form>) => {
    setSaveErrors({});
    setBanner(null);
    setForm((current) => (current ? { ...current, ...patch } : current));
  };

  const save = async () => {
    if (!edit) return;
    setIsSaving(true);
    try {
      const result = await adapter.runAlertCommand(edit, changes);
      onOpenChange(false);
      onSaved(result.summary);
    } catch (err) {
      const errors = extractFieldErrors(err);
      if (errors.length) {
        setSaveErrors(
          Object.fromEntries(errors.map((e) => [e.field, e.message])),
        );
      } else {
        setBanner(refusalWords(err) ?? "The rule was not saved. Try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const errorProps = (field: string) =>
    fieldErrors[field]
      ? {
          "aria-invalid": true as const,
          "aria-describedby": `rule-${field}-error`,
        }
      : {};
  const errorLine = (field: string) =>
    fieldErrors[field] ? (
      <p id={`rule-${field}-error`} className="text-sm text-red-600">
        {fieldErrors[field]}
      </p>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit rule{rule ? `: ${rule.label}` : ""}</DialogTitle>
        </DialogHeader>
        {(banner || errorCount > 0) && (
          <p
            role="alert"
            className="rounded-md border border-destructive px-3 py-2 text-sm text-destructive"
          >
            {banner ??
              (errorCount === 1
                ? "1 field needs a fix: see below."
                : `${errorCount} fields need a fix: see below.`)}
          </p>
        )}
        {form && rule && (
          <div className="space-y-4 text-sm">
            <div>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={form.is_enabled}
                  onCheckedChange={(checked) =>
                    set({ is_enabled: checked === true })
                  }
                />
                On
              </label>
              {notes.map((line) => (
                <p key={line} className="mt-1 text-xs text-muted-foreground">
                  {line}
                </p>
              ))}
            </div>
            <div className="space-y-1">
              <p className="font-medium">Goes to</p>
              <RadioGroup
                value={form.owner}
                onValueChange={(value) => set({ owner: value as RuleOwner })}
              >
                <label className="flex items-center gap-2">
                  <RadioGroupItem value="customer_csr" /> The customer's CSR,
                  else the department
                </label>
                <label className="flex items-center gap-2">
                  <RadioGroupItem value="department" /> The department
                </label>
              </RadioGroup>
            </div>
            <div className="space-y-1">
              <Label htmlFor="rule-department">Department</Label>
              <select
                id="rule-department"
                className={selectClass}
                value={form.department_slug}
                onChange={(e) => set({ department_slug: e.target.value })}
                {...errorProps("department_slug")}
              >
                {!departments.some((d) => d.slug === form.department_slug) && (
                  <option value={form.department_slug}>
                    {form.department_slug}
                  </option>
                )}
                {departments.map((department) => (
                  <option key={department.slug} value={department.slug}>
                    {department.name}
                  </option>
                ))}
              </select>
              {errorLine("department_slug")}
            </div>
            <div className="space-y-1">
              <Label htmlFor="rule-due">
                {rule.due_set_by_time ? "Due" : "Due, in working days"}
              </Label>
              {rule.due_set_by_time ? (
                <p id="rule-due" className="text-sm">
                  {rule.due_label}
                </p>
              ) : (
                <>
                  <Input
                    id="rule-due"
                    inputMode="numeric"
                    className="max-w-28"
                    value={form.due_working_days}
                    onChange={(e) =>
                      set({
                        due_working_days: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    {...errorProps("due_working_days")}
                  />
                  {errorLine("due_working_days")}
                </>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="rule-priority">Priority</Label>
              <select
                id="rule-priority"
                className={`${selectClass} max-w-48`}
                value={form.priority}
                onChange={(e) => set({ priority: e.target.value })}
              >
                {PRIORITIES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {changesBlock &&
              changesBlock.kind === "before_after" &&
              changesBlock.before.length > 0 && (
                <div className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-2">
                  <p className="font-medium">Before → after</p>
                  {changesBlock.before.map((before, n) => (
                    <p key={before}>
                      {before} →{" "}
                      {changesBlock.after[n]?.split(": ").slice(1).join(": ")}
                    </p>
                  ))}
                </div>
              )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
